import type { LatLng } from "leaflet";
import _ from "lodash";
import { type ReactNode, useMemo, useRef } from "react";
import { FeatureGroup, LayerGroup } from "react-leaflet";

import { Map } from "@/components";
import { FixtureSelectionProvider } from "@/components/features/map/fixture-selection-provider";
import {
    globalLightModes,
    useBoxSelector,
    useDrawSelection,
    useFixtureMovement,
    useFixturePlacement,
    useLightSelection,
    useMapClick,
    useMapElements,
    useViewportCulling,
} from "@/hooks/map";
import { useLatestRef } from "@/hooks/utils";
import type { Light, MapSettings } from "@/models";
import type { FloorSpaceCatalog } from "@/models/floor-space-catalog";
import type { MarkerPosition } from "@/models/marker-position";
import type {
    LightGroupGeometryModel,
    MapHandle,
    MapMode,
    Option,
} from "@/types";
import { Map as MapUtil } from "@/utils";

import {
    EditPanel,
    LegendCluster,
    MarkerEditPanel,
    SettingsModal,
    ToolsBar,
    ToolsBarStatus,
    TopBar,
} from ".";
import FirmwareLegend from "./FirmwareLegend";

/**
 * Props for the MapContainer component.
 */
interface MapContainerProps {
    /**
     * The array of light models for rendering.
     */
    arrayOfLights: {
        lights: number;
        direction: string;
        spacing: number;
    };
    /**
     * The global lights array.
     */
    lights: Light[];
    /**
     * The mode-scoped lights array.
     */
    modeLights: Light[];
    /**
     * The map settings configuration.
     */
    mapSettings: MapSettings;
    /**
     * The top bar display options.
     */
    topBarOptions: {
        autoHideTopBar: boolean;
        showLastBleId: boolean;
        gridVisible: boolean;
        snapping: boolean;
        apertureVisible: boolean;
        backgroundVisible: boolean;
        lightOpacity: number;
    };
    /**
     * The current map mode.
     */
    mode: MapMode;
    /**
     * Callback to update global lights.
     */
    updateLights: (lights: Light[]) => void;
    /**
     * The toast notification callback.
     */
    toastr: (message: string, type?: string) => void;
    /**
     * Callback to select lights.
     */
    selectLights: (lights: Light | Light[], selectionType?: string) => void;
    /**
     * Callback to deselect lights.
     */
    deselectLights: (lights: Light | Light[]) => void;
    /**
     * Callback to update mode lights.
     */
    updateModeLights: (lights: Light | Light[]) => void;
    /**
     * Whether paste mode is active.
     */
    pasteModeActivated: boolean;
    /**
     * Callback to paste lights at a given point.
     */
    paste: (point: LatLng) => void;
    /**
     * Whether filter mode is active.
     */
    filtering: boolean;
    /**
     * Whether search mode is active.
     */
    searching: boolean;
    /**
     * Whether light selection is enabled.
     */
    selectionEnabled: boolean;
    /**
     * The current highlight row/column configuration.
     */
    highlight: {
        column: number | null;
        row: number | null;
    };
    /**
     * Callback to set the highlight position.
     */
    setHighlight: (highlight: {
        column?: number | null;
        row?: number | null;
    }) => void;
    /**
     * Callback to change the current map mode.
     */
    changeMode: (name: string, props: Record<string, unknown>) => void;
    /**
     * Callback to add new lights to the map.
     */
    addLights: (lights: Light[]) => void;
    /**
     * The array of control group geometries.
     */
    groupGeometries: LightGroupGeometryModel[];
    /**
     * The group geometry currently being edited.
     */
    editingGroupGeometry: Option<LightGroupGeometryModel>;
    /**
     * The currently selected drawing tool.
     */
    selectedTool: { name: string } | null;
    /**
     * Callback to set the selected tool.
     */
    setSelectedTool: (tool: { name: string } | null) => void;
    /**
     * The marker positions keyed by unique ID.
     */
    markers: Record<string, MarkerPosition>;
    /**
     * The mode-scoped marker positions keyed by unique ID.
     */
    modeMarkers: Record<string, MarkerPosition>;
    /**
     * Callback to update marker positions in the store.
     */
    updateMarkers: (markers: Record<string, MarkerPosition>) => void;
    /**
     * Callback to add a temporary marker to mode state.
     */
    addTemporaryMarker: (marker: MarkerPosition) => void;
    /**
     * Callback to update mode-scoped marker positions.
     */
    updateModeMarkers: (markers: Record<string, MarkerPosition>) => void;
    /**
     * Callback to select a marker.
     */
    selectMarker: (marker: MarkerPosition) => void;
    /**
     * The FloorSpaceCatalog lookup map keyed by floorSpaceCatalogID.
     */
    floorSpaceCatalogMap: Record<number, FloorSpaceCatalog>;
}

/**
 * Map container component responsible for rendering the map and managing interactions.
 *
 * @param {MapContainerProps} props - The props for the MapContainer component.
 * @returns {ReactNode} The rendered MapContainer component.
 */
const MapContainer = ({
    arrayOfLights,
    lights: lightsProp,
    modeLights,
    mapSettings,
    topBarOptions,
    mode,
    updateLights,
    toastr,
    selectLights,
    deselectLights,
    updateModeLights,
    pasteModeActivated,
    paste,
    filtering,
    searching,
    selectionEnabled,
    highlight,
    setHighlight,
    changeMode,
    addLights,
    groupGeometries,
    editingGroupGeometry,
    selectedTool,
    setSelectedTool,
    markers,
    modeMarkers,
    updateMarkers,
    addTemporaryMarker,
    updateModeMarkers,
    selectMarker,
    floorSpaceCatalogMap,
}: MapContainerProps): ReactNode => {
    const mapRef = useRef<MapHandle | null>(null);
    const lights = useMapLights({ mode, modeLights, lights: lightsProp });

    const allMarkers = useMemo(
        () => ({ ...markers, ...modeMarkers }),
        [markers, modeMarkers],
    );

    const lightsRef = useLatestRef(lights);
    const markersRef = useLatestRef(allMarkers);

    const {
        isLightInViewport,
        isMarkerInViewport,
        legendsVisible,
        onUpdateViewport,
        setLegendVisibility,
        showFlag,
    } = useViewportCulling({ mapRef });

    const rendersGlobalLights = globalLightModes.includes(mode.name);

    const { boxSelectorRef, onBoxCreated } = useBoxSelector({
        deselectLights,
        disabled: rendersGlobalLights,
        mapRef,
        mode,
        modeLights,
        pasteModeActivated,
        selectedTool,
        selectLights,
        setSelectedTool,
    });

    useDrawSelection({
        mapRef,
        mode,
        modeLights,
        pasteModeActivated,
        selectLights,
    });

    const { onSelectLight } = useLightSelection({
        filtering,
        lights,
        mode,
        pasteModeActivated,
        searching,
        selectionEnabled,
        selectLights,
    });

    const { onAddFixtures } = useFixturePlacement({
        addLights,
        addTemporaryMarker,
        arrayOfLights,
        lightsRef,
        mapSettings,
        markersRef,
        mode,
        selectMarker,
        snapping: topBarOptions.snapping,
        toastr,
    });

    const { onMoveLight, onMoveMarker, onSelectMarker, snapPoint } =
        useFixtureMovement({
            lightsRef,
            mapRef,
            mapSettings,
            markers,
            mode,
            modeMarkers,
            pasteModeActivated,
            selectMarker,
            selectionEnabled,
            snapping: topBarOptions.snapping,
            toastr,
            updateLights,
            updateMarkers,
            updateModeLights,
            updateModeMarkers,
        });

    const { onMapClick } = useMapClick({
        changeMode,
        mapRef,
        mode,
        onAddFixtures,
        paste,
        pasteModeActivated,
        setHighlight,
    });

    const elements = useMapElements({
        apertureVisible: topBarOptions.apertureVisible,
        filtering,
        floorSpaceCatalogMap,
        isLightInViewport,
        isMarkerInViewport,
        legendsVisible,
        lightOpacity: topBarOptions.lightOpacity,
        lights: lightsProp,
        mapSettings,
        markers,
        mode,
        modeLights,
        modeMarkers,
        onMoveLight,
        onMoveMarker,
        onSelectLight,
        onSelectMarker,
        searching,
        setLegendVisibility,
        showFlag,
        showLastBleId: topBarOptions.showLastBleId,
        snapPoint,
    });

    const selectedLights = useMemo(
        () =>
            _.filter(
                rendersGlobalLights ? lightsProp : modeLights,
                (light) => light.selected,
            ),
        [rendersGlobalLights, lightsProp, modeLights],
    );

    return (
        <div className="route">
            <TopBar />
            <ToolsBar>
                <ToolsBarStatus />
                {selectedLights.length > 0 && (
                    <EditPanel
                        key="edit-panel"
                        selectedLights={_.cloneDeep(selectedLights)}
                    />
                )}
                {selectedLights.length === 0 && <MarkerEditPanel />}
            </ToolsBar>

            <Map
                mapSettings={mapSettings}
                highlight={highlight}
                onMapClick={onMapClick}
                ref={mapRef}
                onUpdateViewport={onUpdateViewport}
                viewport={MapUtil.viewport}
                showGrid={topBarOptions.gridVisible}
                showBackground={topBarOptions.backgroundVisible}
                mode={mode}
                groupGeometries={groupGeometries}
                editingGroupGeometry={editingGroupGeometry}
                selectedTool={selectedTool}
            >
                {mode.name === "firmware" && (
                    <FirmwareLegend
                        lights={lightsProp}
                        floorSpaceCatalogMap={floorSpaceCatalogMap}
                    />
                )}
                <FeatureGroup>
                    <FixtureSelectionProvider>
                        {elements.lights}
                        {elements.markers}
                        <LayerGroup>
                            <LegendCluster visible={legendsVisible}>
                                {elements.legends}
                            </LegendCluster>
                            <LegendCluster visible={showFlag}>
                                {elements.flags}
                            </LegendCluster>
                        </LayerGroup>
                        {elements.selectedLights}
                    </FixtureSelectionProvider>
                    <Map.BoxSelector
                        ref={boxSelectorRef}
                        onCreated={onBoxCreated}
                    />
                </FeatureGroup>
            </Map>
            <SettingsModal />
        </div>
    );
};

interface MapLightsProps {
    mode: MapMode;
    modeLights: Light[];
    lights: Light[];
}

const useMapLights = ({ mode, modeLights, lights }: MapLightsProps) =>
    useMemo(() => {
        switch (mode.name) {
            case "select":
                return modeLights;
            default:
                return [...lights, ...modeLights];
        }
    }, [mode.name, lights, modeLights]);

export { MapContainer };
